import { Box, Container, Divider, FormLabel } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import ButtonCreateNew from 'components/ui/ButtonCreateNew';
import CheckboxSelect from 'components/ui/CheckboxSelect/CheckboxSelect';
import FilterGroup from 'components/ui/FilterGroup';
import HeaderBar from 'components/ui/HeaderBar';
import SearchInput from 'components/ui/SearchInput';
import ActiveTag from 'components/ui/ActiveTag';
import DateTime from 'components/ui/DateTime';
import { usePermissionsContext } from 'contexts/PermissionsContext';
import ServerDynamicTableProvider from 'contexts/server-table/ServerDynamicTableContext';
import useServerTableUrlState from 'hooks/server-table/useServerTableUrlState';
import React from 'react';
import CompaniesList from '../components/CompaniesList';

type CompanyFilterKey = 'activeIn';

const companyFilterDefaults: Record<CompanyFilterKey, string> = {
  activeIn: '',
};

export default function PageCompanies() {
  const { ability } = usePermissionsContext();
  const {
    pagination,
    globalFilter,
    filters,
    setPagination,
    setGlobalFilter,
    setFilter,
  } = useServerTableUrlState<CompanyFilterKey>({
    filterDefaults: companyFilterDefaults,
  });

  const columns = React.useMemo<ColumnDef<Record<string, unknown>>[]>(
    () => [
      {
        header: 'Nome',
        accessorKey: 'name',
      },
      {
        header: 'Status',
        accessorKey: 'active',
        cell: ({ row }) => <ActiveTag isActive={Boolean(row.original.active)} />,
      },
      {
        header: 'Membros',
        accessorKey: '_count.recipients',
      },
      {
        header: 'Listas',
        accessorKey: '_count.lists',
      },
      {
        header: 'Cadastrado em',
        accessorKey: 'createdAt',
        cell: ({ row }) => <DateTime date={row.original.createdAt as string} />,
      },
    ],
    [],
  );

  const statusOptions = [
    { value: 'true', label: 'Ativo' },
    { value: 'false', label: 'Inativo' },
  ];

  return (
    <ServerDynamicTableProvider
      columns={columns}
      pagination={pagination}
      globalFilter={globalFilter}
      onPaginationChange={setPagination}
      onGlobalFilterChange={setGlobalFilter}
      resource={{
        resourceName: 'companies',
        params: {
          page: pagination.pageIndex + 1,
          pageSize: pagination.pageSize,
          q: globalFilter || undefined,
          activeIn: filters.activeIn || undefined,
        },
      }}
    >
      <HeaderBar 
        title="Clientes"
        description="Visualize a lista de clientes Patri"
      >
      { ability.can('create', 'Company') && (
        <ButtonCreateNew 
          title="Cadastrar novo"
          to="/clientes/novo"
        />
      )}
      </HeaderBar>
      
      <Divider />
      
      <Container flexDirection="column">
        <FilterGroup justifyContent="space-between" pos="relative">
          <Box flex={1} minW="180px">
            <FormLabel>Buscar</FormLabel>
            <SearchInput
              placeholder="Busque clientes..."
              value={globalFilter}
              onChange={(value) => setGlobalFilter(String(value))}
            />
          </Box>
          <Box>
            <FormLabel>Filtrar por status</FormLabel>
            <CheckboxSelect
              placeholder="Todos os status"
              options={statusOptions}
              value={statusOptions.filter((option) =>
                parseCsv(filters.activeIn).includes(option.value),
              )}
              onChange={(value) =>
                setFilter(
                  'activeIn',
                  value.map((item) => String(item.value)).join(','),
                )
              }
            />
          </Box>
        </FilterGroup>

        <CompaniesList />
      </Container>
    </ServerDynamicTableProvider>
  )
}

const parseCsv = (value?: string) => {
  if (!value) {
    return [];
  }

  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};
